import {
  CUSTOMER_FORGOT_PASSWORD_SLUG,
  CUSTOMER_LOGIN_SLUG,
  CUSTOMER_SIGNUP_SLUG,
  type CustomerRouteMode,
} from '../routes'
import { buildAuthLink } from '../helpers/formatters'
import { useShopI18n } from '../../i18n'
import { useCustomerAuthForm } from '../hooks/useCustomerAuthForm'
import type { CustomerSignupInput } from '../types'
import {
  CustomerAuthFormBlock,
  CustomerAuthSwitchLink,
} from './customer-auth'
import type { CustomerLoginActionResult } from '../hooks/useCustomerAuthActions'
import {
  Auth2FAView,
  AuthCardShell,
  AuthLoginView,
  AuthRegisterView,
  useAuthFlowEffects,
  useAuthFlowReducer,
  useAuthSubmitHandlers,
  useAuthViewModel,
} from './customer-auth/auth-flow'

type AuthMode = Extract<CustomerRouteMode, 'login' | 'signup'>

type CustomerAuthPageProps = {
  mode: AuthMode
  error: string
  busy: boolean
  initializing: boolean
  nextPath: string
  nextPathReady: boolean
  onClearError: () => void
  onNavigate: (targetPath: string) => void
  onLogin: (input: { identifier: string; password: string }) => Promise<CustomerLoginActionResult>
  onVerifyLoginSecondFactor: (input: { sessionId: string; code: string }) => Promise<CustomerLoginActionResult>
  onVerifyLoginSecondFactorWithPasskey: (input: { sessionId: string }) => Promise<CustomerLoginActionResult>
  onSignup: (input: CustomerSignupInput) => Promise<boolean>
}

export const CustomerAuthPage = ({
  mode,
  error,
  busy,
  initializing,
  nextPath,
  nextPathReady,
  onClearError,
  onNavigate,
  onLogin,
  onVerifyLoginSecondFactor,
  onVerifyLoginSecondFactorWithPasskey,
  onSignup,
}: CustomerAuthPageProps) => {
  const { t, locale, toLocalizedPath } = useShopI18n()
  const effectiveNextPath = nextPathReady ? nextPath : toLocalizedPath('/dashboard')

  const [flowState, dispatch] = useAuthFlowReducer(mode)
  useAuthFlowEffects({
    mode,
    dispatch,
  })

  const submitHandlers = useAuthSubmitHandlers({
    nextPath: effectiveNextPath,
    normalizedTwoFactorCode: flowState.twoFactorCode.replace(/\s+/g, '').trim(),
    twoFactorChallenge: flowState.twoFactorChallenge,
    dispatch,
    onClearError,
    onNavigate,
    onLogin,
    onVerifyLoginSecondFactor,
    onVerifyLoginSecondFactorWithPasskey: onVerifyLoginSecondFactorWithPasskey,
    onSignup,
    invalidTwoFactorCodeLabel: t('customer.auth.login.2fa.invalidCode'),
  })

  const formState = useCustomerAuthForm({
    mode,
    nextPath: effectiveNextPath,
    onClearError,
    onNavigate,
    onLogin: submitHandlers.handleLoginSubmit,
    onLoginTwoFactorChallenge: submitHandlers.handleTwoFactorChallenge,
    onSignup: submitHandlers.handleSignupSubmit,
    botProtectionProviderId: flowState.botProtectionProviderId,
  })

  const viewModel = useAuthViewModel({
    state: flowState,
    formState,
    t,
  })

  const authBody = viewModel.showTwoFactorCard ? (
    <Auth2FAView
      code={flowState.twoFactorCode}
      codeError={viewModel.twoFactorCodeError}
      submitError={flowState.twoFactorError}
      submitting={flowState.twoFactorSubmitting}
      customerAreaLabel={t('common.customerArea')}
      title={t('customer.auth.login.2fa.title')}
      subtitle={t('customer.auth.login.2fa.subtitle')}
      codeLabel={t('customer.auth.login.2fa.codeLabel')}
      codePlaceholder={t('customer.auth.login.2fa.codePlaceholder')}
      submitLabel={t('customer.auth.login.2fa.submit')}
      submittingLabel={t('customer.auth.login.2fa.submitting')}
      passkeyLabel={t('customer.auth.login.2fa.passkey')}
      passkeySubmittingLabel={t('customer.auth.login.2fa.passkeySubmitting')}
      passkeyHintLabel={t('customer.auth.login.2fa.passkeyHint')}
      showPasskeyButton={viewModel.showPasskeyButton}
      onPasskeySubmit={submitHandlers.handleTwoFactorPasskeySubmit}
      backLabel={t('customer.auth.login.2fa.back')}
      onCodeChange={submitHandlers.handleTwoFactorCodeChange}
      onCodeBlur={submitHandlers.handleTwoFactorCodeBlur}
      onSubmit={submitHandlers.handleTwoFactorSubmit}
      onBack={submitHandlers.handleTwoFactorBack}
    />
  ) : (
    <CustomerAuthFormBlock
      busy={busy}
      initializing={initializing}
      botProtectionEnabled={flowState.botProtectionEnabled}
      botProtectionProviderId={flowState.botProtectionProviderId}
      botProtectionClientConfig={flowState.botProtectionClientConfig}
      locale={locale}
      t={t}
      formState={formState}
      onForgotPassword={() => onNavigate(toLocalizedPath(`/${CUSTOMER_FORGOT_PASSWORD_SLUG}`))}
      onGoToLogin={() => onNavigate(buildAuthLink(CUSTOMER_LOGIN_SLUG, effectiveNextPath, toLocalizedPath))}
      onGoToHome={() => onNavigate(toLocalizedPath('/'))}
    />
  )

  const authCard = (
    <AuthCardShell
      error={error}
      closeLabel={t('common.close')}
      onCloseError={onClearError}
    >
      {authBody}

      {viewModel.showTwoFactorCard ? null : viewModel.isSignup ? (
        <CustomerAuthSwitchLink
          textPrefix={t('customer.auth.hasAccount')}
          linkLabel={t('customer.auth.toLogin')}
          href={buildAuthLink(CUSTOMER_LOGIN_SLUG, effectiveNextPath, toLocalizedPath)}
        />
      ) : (
        <CustomerAuthSwitchLink
          textPrefix={t('customer.auth.missingAccount')}
          linkLabel={t('customer.auth.toSignup')}
          href={buildAuthLink(CUSTOMER_SIGNUP_SLUG, effectiveNextPath, toLocalizedPath)}
        />
      )}
    </AuthCardShell>
  )

  return (
    <div className="relative min-h-screen overflow-hidden bg-(--color-bg-page-secondary)">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_16%,rgba(15,23,42,0.06),transparent_36%),radial-gradient(circle_at_86%_26%,rgba(2,132,199,0.08),transparent_40%)]" />
      </div>

      <div className="relative z-10">
        <div className="ca-page ca-theme-transition py-3 sm:py-4 lg:py-5">
          <div className="mx-auto w-full max-w-[1180px] px-4 sm:px-6 lg:px-8">
            <section>
              {viewModel.isSignup ? (
                <AuthRegisterView
                  customerAreaLabel={t('common.customerArea')}
                  title={viewModel.title}
                  subtitle={viewModel.subtitle}
                  authCard={authCard}
                  previewLabel={t('customer.auth.preview.label')}
                  sidebarTitle={t('customer.auth.sidebarTitle')}
                  sidebarDescription={t('customer.auth.sidebarDescription')}
                  stepOfLabel={t('customer.auth.stepOf', {
                    current: formState.signupStep,
                    total: formState.totalSignupSteps,
                  })}
                  currentStepLabel={viewModel.currentSignupStepLabel}
                  currentStepDescription={viewModel.currentSignupStepDescription}
                  accountSupportLabel={t('common.accountSupport')}
                  trustHint={viewModel.trustHint}
                  trustItems={viewModel.trustItems}
                />
              ) : (
                <AuthLoginView
                  customerAreaLabel={t('common.customerArea')}
                  title={viewModel.title}
                  subtitle={viewModel.subtitle}
                  authCard={authCard}
                />
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}
